import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Dynamically load Google API client
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '95462c7d7401-dummyclientid.apps.googleusercontent.com',
          callback: handleGoogleResponse,
        });

        // Determine available width based on container to make it mobile-responsive
        const container = document.getElementById('google-signin-btn-native');
        const containerWidth = container ? container.offsetWidth : 0;
        let buttonWidth = 380;
        if (containerWidth > 0) {
          buttonWidth = Math.max(200, Math.min(400, containerWidth));
        } else {
          // Fallback based on viewport width
          const vw = window.innerWidth;
          buttonWidth = vw < 480 ? Math.max(200, Math.min(380, vw - 80)) : 380;
        }

        window.google.accounts.id.renderButton(
          container,
          { theme: 'outline', size: 'large', width: buttonWidth.toString() }
        );
      }
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleGoogleResponse = async (response) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/google', { idToken: response.credential });
      login(data);
      navigate(data.role === 'ADMIN' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMockGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/google', { idToken: 'test-google-token' });
      login(data);
      navigate(data.role === 'ADMIN' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', form);
      login(data);
      navigate(data.role === 'ADMIN' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  const isDummyClient = !process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_CLIENT_ID.includes('dummy');
  const showDevLogin = process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ width: 52, height: 52, background: 'var(--navy)', borderRadius: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '.75rem' }}>
            <span style={{ color: 'var(--gold)', fontSize: '1.4rem', fontFamily: 'Playfair Display, serif', fontWeight: 700 }}>T</span>
          </div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--navy)' }}>Welcome Back</h1>
          <p style={{ color: 'var(--muted)', fontSize: '.9rem', marginTop: '.25rem' }}>Sign in to MyTaxNiti</p>
        </div>

        <div className="auth-card" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {error && <div className="alert alert-error">⚠ {error}</div>}
          
          {isDummyClient && showDevLogin && (
            <div style={{ background: '#fff3cd', color: '#856404', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem', border: '1px solid #ffeeba', lineHeight: 1.4 }}>
              <strong>ℹ Local Dev Mode:</strong> Google Sign-In is using a dummy client ID. Click <strong>Google Developer Login</strong> below to test, or configure a valid client ID in your <code>.env</code> file.
            </div>
          )}
          <form onSubmit={submit}>
            <div className="form-grid" style={{ gridTemplateColumns: '1fr', gap: '1rem' }}>
              <div className="field">
                <label>Email Address<span className="req">*</span></label>
                <input name="email" type="email" placeholder="you@email.com"
                  value={form.email} onChange={handle} required />
              </div>
              <div className="field">
                <label>Password<span className="req">*</span></label>
                <input name="password" type="password" placeholder="••••••••"
                  value={form.password} onChange={handle} required />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading}
              style={{ width: '100%', marginTop: '1.5rem', justifyContent: 'center' }}>
              {loading ? <><span className="spinner" /> Signing in...</> : 'Sign In →'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0.5rem 0' }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 500 }}>or continue with</span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          </div>

          {/* Google Sign-in Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', alignItems: 'center' }}>
            {/* Native Google Button Container */}
            <div id="google-signin-btn-native" style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />

            {/* Custom/Fallback Developer Google Button */}
            {showDevLogin && (
              <button
                type="button"
                onClick={handleMockGoogleLogin}
                disabled={loading}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  background: '#ffffff',
                  border: '1px solid #dadce0',
                  borderRadius: '4px',
                  padding: '0.6rem 1rem',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#3c4043',
                  cursor: 'pointer',
                  transition: 'background-color .2s, box-shadow .2s',
                  outline: 'none',
                }}
                onMouseOver={e => { e.currentTarget.style.backgroundColor = '#f7f8f8'; e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)'; }}
                onMouseOut={e => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.59.102-1.167.282-1.707V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.333z" fill="#FBBC05"/>
                  <path d="M9 3.579c1.32 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.454 0 2.414 2.04 0 4.96l3.007 2.332C3.718 5.163 5.702 3.579 9 3.579z" fill="#EA4335"/>
                </svg>
                Google Developer Login
              </button>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: '0.25rem', fontSize: '.85rem', color: 'var(--muted)' }}>
            Don't have an account? <Link to="/register" style={{ color: 'var(--gold)', fontWeight: 600 }}>Register here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
